import { debounce } from "lodash";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Box } from "../../../common/components";
import { ProductImage } from "../../../components/StudioToolbar/ProductImage";
import StudioToolbar, {
  STUDIO_TOOLBAR_HEIGHT,
} from "../../../components/StudioToolbar/StudioToolbar";
import { useAppLoading } from "../../../context/AppLoading/useAppLoading";
import OrderService from "../../../service/OrderService";
import { ROUTES } from "../../../constant/common.constant";
import { usePurchasedDesignFetcher } from "../../../utils/PurchasedDesignsFetcherHooks";
import { DESIGN_CHANGE_ENUM, useDesignState } from "../../../utils/ReactHooks";
import { useThemeBreakpoints } from "../../../utils/themeUtils";
import StudioLeftSidebar from "../../StudioSidebarContainer/StudioLeftSidebar/StudioLeftSidebar";
import { StudioRightSidebar } from "../../StudioSidebarContainer/StudioRightSidebar";
import MobileBottomBar from "../components/MobileBottomBar";
import ProjectDesignSelector from "../ProjectDesignSelector/ProjectDesignSelector";
import { getBackRoutes } from "./utils";
import { StudioContextProvider } from "../../../context/Studio/StudioContextProvider";

const PurchasedDesignStudioContainer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { designId, orderId, orderType, historyFrom } = useParams();
  const containerRef = useRef<HTMLDivElement>(null);

  const { isMobile } = useThemeBreakpoints();
  const { setIsAppLoading } = useAppLoading();

  const [purchasedDesign, setPurchasedDesign] = useState<any>();
  const [purchasedDesignLoaded, setPurchasedDesignLoaded] = useState(false);

  const { fetchPurchasedDesignAsAdmin } = usePurchasedDesignFetcher();

  useEffect(() => {
    async function fetchPurchasedDesign() {
      const fetchedPurchasedDesign =
        await fetchPurchasedDesignAsAdmin(designId);
      setPurchasedDesign(fetchedPurchasedDesign);
      setPurchasedDesignLoaded(true);
    }

    fetchPurchasedDesign();
  }, [designId, fetchPurchasedDesignAsAdmin]);

  //No multiplayer when editing purchased designs
  const previewContainerRef = useRef<HTMLDivElement>(null);

  const designState = useDesignState(designId, true);

  const {
    isProductDefLoading,
    hasLoadingFailed,
    loadingFailedErrorKey,
    designFromDesignState,
  } = designState;

  const onDesignChangeAlsoSyncWithOrder = useCallback(
    async (action: DESIGN_CHANGE_ENUM, data: any) => {
      designState.onDesignChange(action, data)?.then(async () => {
        const event = {
          name: "purchased_design_updated", //This needs to be same as the HISTORY_EVENT_ENUM.PURCHASED_DESIGN_UPDATED in backend
          data: {
            new: {
              designId: designId,
              action: action,
              data: data,
            },
          },
        };
        if (orderType === "preorder") {
          await OrderService.syncPurchasedOrderWithDesign(orderId, event);
        }
      });
    },
    [designState, designId, orderType, orderId]
  );

  const productName = designFromDesignState?.product.name;

  //If design is not found in backend database, then we go home
  useEffect(() => {
    if (purchasedDesignLoaded && !purchasedDesign) {
      toast.warn(t("design.not_found"));
      navigate(ROUTES.HOME);
      return;
    }
  }, [purchasedDesign, purchasedDesignLoaded, navigate, t]);

  //If loading has failed from designState, we go to home page and let user know there was an issue
  useEffect(() => {
    if (hasLoadingFailed) {
      toast.error(t(`errors.toast.${loadingFailedErrorKey}`), {
        toastId: loadingFailedErrorKey,
      });
      navigate(ROUTES.HOME);
      return;
    }
  }, [hasLoadingFailed, loadingFailedErrorKey, t, navigate]);

  useEffect(() => {
    if (isProductDefLoading || !purchasedDesignLoaded) {
      setIsAppLoading(true);
    } else {
      setIsAppLoading(false);
    }
  }, [isProductDefLoading, purchasedDesignLoaded, setIsAppLoading]);

  if (!designId || !productName) {
    return null;
  }

  return (
    <StudioContextProvider
      value={{
        designState: {
          ...designState,
          onDesignChange: debounce(onDesignChangeAlsoSyncWithOrder, 0),
        },
        designId,
        productName,
        projectName: purchasedDesign?.original_project_name,
        projectId: purchasedDesign?.original_project_id,
        teamId: purchasedDesign?.original_team_id,
        editorConfig: {
          toolbar: {
            breadcrumbs: {
              ...getBackRoutes(t, historyFrom, orderType, orderId),
            },
            hideDesignMenu: true,
            hideMiddleSection: false,
            hideAddToCartButton: true,
            hideCurrentUsers: true,
            hideInviteUserButton: true,
            hideDesignSelector: true,
          },
        },
      }}
    >
      <Box ref={containerRef}>
        <Box>
          <StudioToolbar designName={purchasedDesign?.original_design_name} />
        </Box>

        <Box
          sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" } }}
          minHeight={`calc(100dvh - ${STUDIO_TOOLBAR_HEIGHT})`}
        >
          <Box>
            <ProjectDesignSelector />
          </Box>
          <Box
            sx={{ display: "flex", overflow: "hidden" }}
            width={"100%"}
            flex={1}
          >
            <React.Fragment>
              {/* {cursorsOfUsersConnected.map((c) => (
                <MultiplayerCursor
                  previewContainerRef={previewContainerRef}
                  xCoordinates={c.coordinates.x}
                  yCoordinates={c.coordinates.y}
                  color={c.color}
                  key={c.userId}
                />
              ))} */}
              {!isMobile && <StudioLeftSidebar />}
              <ProductImage previewContainerRef={previewContainerRef} />
              {!isMobile && <StudioRightSidebar />}
            </React.Fragment>
          </Box>
          {isMobile && <MobileBottomBar />}
        </Box>
      </Box>
    </StudioContextProvider>
  );
};

export default PurchasedDesignStudioContainer;
